import { useContext, useEffect } from 'react';
import { UserContext } from './useUser';
import { useNavigate } from 'react-router-dom';
import { Role } from '../../enums/Role';
import Layout from './layout/Layout';
import ErrorPage from '../../pages/ErrorPage';
import { PageAccessStatus, canAccessPageStatus, isDemoModeEnabled } from '../../utils/featureAccess';

const NoPermissionPage = () => {
  return (<Layout>
    <ErrorPage variant='403' />
  </Layout>);
};

interface AuthGuardProps {
  children: JSX.Element;
  optionalChildren?: JSX.Element;
  permissions: Role[];
  requiredModule?: string;
  /** Completeness status for pages still in demo/preview. See src/utils/featureAccess.ts. */
  status?: PageAccessStatus;
}

const AuthGuard: React.FC<AuthGuardProps> = ({ children, optionalChildren, permissions, requiredModule, status }) => {
  const navigate = useNavigate();
  const { hasPermissionTo, hasPermissions, selectedStore, hasPremiumModuleAccess } = useContext(UserContext);

  // Demo Mode bypasses page-level permission/module checks for demo-flagged routes —
  // the user still needs to be logged in (hasPermissions()/selectedStore checks above are untouched).
  const bypassForDemo = status === 'demo' && isDemoModeEnabled();
  const hasStatusAccess = canAccessPageStatus(status, {
    hostname: typeof window === 'undefined' ? undefined : window.location.hostname,
    hasAdminPermission: hasPermissionTo(['Admin']),
  });

  useEffect(() => {
    if (!hasPermissions() || !selectedStore) {
      navigate('/login');
    }
  }, []);

  if (!hasPermissions()) {
    return null;
  }

  if (!hasStatusAccess) {
    return <NoPermissionPage />;
  }

  if (!bypassForDemo && permissions && permissions.length > 0 && !hasPermissionTo(permissions)) {
    return <NoPermissionPage />;
  }

  if (!bypassForDemo && requiredModule && !hasPremiumModuleAccess(requiredModule)) {
    return <NoPermissionPage />;
  }

  return children;
};

export default AuthGuard;